import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { CircleCheck, Calendar, Phone, User, FileText, Loader, ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "@/components/common/Link";
const benefits = [];
function InspectionForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    handleSubmit,
    control
  } = useWvcForm();
  const [showAdditional, setShowAdditional] = useState(false);
  useEffect(() => {
    if (isSubmitted && !submitError) {
      window.location.href = "/thank-you";
    }
  }, [isSubmitted, submitError]);
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-900 font-medium">Full Name</FormLabel>
              <FormControl>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 size-4 text-slate-400" />
                  <Input placeholder="John Doe" className="pl-9 bg-slate-50 border-slate-200 focus:border-orange-500 focus:bg-white transition-all rounded-sm" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required",
        pattern: {
          value: /^[0-9-+() ]*$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-900 font-medium">Phone Number</FormLabel>
              <FormControl>
                <div className="relative">
                  <Phone className="absolute left-3 top-2.5 size-4 text-slate-400" />
                  <Input type="tel" placeholder="(515) 123-4567" className="pl-9 bg-slate-50 border-slate-200 focus:border-orange-500 focus:bg-white transition-all rounded-sm" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="zip_code" rules={{
      required: "ZIP Code is required",
      pattern: {
        value: /^\d{5}$/,
        message: "Must be 5 digits"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-slate-900 font-medium">ZIP Code</FormLabel>
            <FormControl>
              <Input placeholder="50309" className="bg-slate-50 border-slate-200 focus:border-orange-500 focus:bg-white transition-all rounded-sm" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="border border-slate-200 rounded-sm overflow-hidden">
        <Button type="button" variant="ghost" onClick={() => setShowAdditional(!showAdditional)} className="w-full flex items-center justify-between p-3 h-auto bg-slate-50 hover:bg-slate-100 transition-colors text-sm font-medium text-slate-700 rounded-sm">
          <span>Additional Details (Optional)</span>
          {showAdditional ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
        </Button>
        
        {showAdditional && <div className="p-4 space-y-4 bg-white border-t border-slate-200">
            <FormField control={control} name="preferred_date" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-slate-900 font-medium">Preferred Inspection Date</FormLabel>
                <FormControl>
                  <div className="relative">
                    <SafeHydrate><Calendar className="absolute left-3 top-2.5 size-4 text-slate-400" /></SafeHydrate>
                    <Input type="date" className="pl-9 bg-slate-50 border-slate-200 focus:border-orange-500 focus:bg-white transition-all block w-full rounded-sm" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />

            <FormField control={control} name="additional_details" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-slate-900 font-medium">Inspection Notes</FormLabel>
                <FormControl>
                  <div className="relative">
                    <FileText className="absolute left-3 top-3 size-4 text-slate-400" />
                    <textarea className="flex min-h-[80px] w-full rounded-sm border border-slate-200 bg-slate-50 px-3 py-2 pl-9 text-sm ring-offset-background placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 focus:bg-white transition-all" placeholder="Tell us about any moisture, mold, or odor concerns..." {...field} />
                  </div>
                </FormControl>
                <FormDescription className="text-slate-500 text-xs font-mono">
                  Briefly describe the issues you're seeing in your crawl space.
                </FormDescription>
                <FormMessage />
              </FormItem>} />
          </div>}
      </div>

      {submitError && <div className="p-3 bg-red-50 border border-red-200 rounded-sm flex items-start gap-2 text-red-800 text-sm">
          <span className="mt-0.5"></span>
          <p>{submitError}</p>
        </div>}

      <div className="py-4 px-6 bg-slate-50 border border-slate-200 rounded-lg text-center">
        <p className="text-sm text-slate-600 font-medium leading-relaxed">
          Most inspections can be scheduled within 24-48 hours throughout the Des Moines metro area.
        </p>
      </div>

      <Button type="submit" disabled={isSubmitting} className="w-full h-16 sm:h-20 px-8 inline-flex items-center justify-center bg-secondary ring-2 ring-secondary/50 shadow-2xl shadow-secondary/30 text-white font-bold rounded-lg hover:scale-105 transition-transform relative overflow-hidden group">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Analyzing...
          </> : <>
            <span className="relative z-10 flex items-center justify-center gap-2 tracking-tight text-lg sm:text-xl">
              Request Free Crawl Space Inspection
              <ArrowRight className="size-5 group-hover:translate-x-1 transition-transform" />
            </span>
            <span className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
          </>}
      </Button>
      
      <p className="text-[10px] font-mono text-center text-slate-400 mt-4">
        BY SUBMITTING, YOU AGREE TO OUR <Link to="#" className="underline hover:text-orange-600">PRIVACY POLICY</Link>. DATA IS ENCRYPTED.
      </p>
    </Form>;
}
export default function ClaimYourFreeInspection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="get-your-free-inspection" className="bg-slate-50 text-slate-900 py-20 relative overflow-hidden font-sans">
      <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615668/img/roofing_texture_dark.png)] opacity-5 bg-cover bg-center mix-blend-multiply pointer-events-none" />
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate-900/10 to-transparent" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={`grid lg:grid-cols-2 gap-12 items-center transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-orange-500/10 border border-orange-500/20 text-orange-600 text-[11px] font-mono font-bold uppercase tracking-widest">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-lg bg-orange-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-lg h-2 w-2 bg-orange-500"></span>
                </span>
                Priority Scheduling Open
              </div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tighter text-slate-900 leading-tight">
                Get a Free Crawl Space Inspection in Des Moines
              </h2>
              <p className="text-lg text-slate-600 max-w-xl leading-relaxed">
                Stop mold and moisture damage before it spreads. Get a comprehensive crawl space inspection to protect your home's health and structural integrity.
              </p>
            </div>

            <div className="space-y-4">
              {benefits.map((benefit, i) => <div key={i} data-index={i} className="flex items-start gap-3 group">
                  <CircleCheck className="size-6 text-teal-700 shrink-0 group-hover:scale-110 transition-transform duration-300" />
                  
                </div>)}
            </div>

            <div className="p-6 rounded-sm bg-white border border-slate-200 shadow-sm">
              <div className="flex items-center gap-4 mb-4">
                <div className="text-sm">
                  <div className="flex text-orange-500 gap-0.5 mb-1">
                    {[1, 2, 3, 4, 5].map(star => <svg key={star} className="size-4 fill-current" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>)}
                  </div>
                  <p className="text-slate-500 font-mono text-[11px] uppercase font-bold">Trusted by 500+ Des Moines Homeowners</p>
                </div>
              </div>
              <p className="text-slate-600 italic text-sm border-l-2 border-orange-600 pl-4 leading-relaxed">
                "The professional we were connected with performed an incredibly thorough audit. They used scientific sensors to show exactly where my moisture issues were. No high-pressure sales, just facts."
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center p-4 bg-slate-100 border border-slate-200 rounded-sm">
              <div className="flex items-center justify-center gap-3">
                <Phone className="size-6 sm:size-8 text-orange-600" />
                <span className="text-lg sm:text-xl font-bold text-slate-900">Call Now: <span className="text-lg sm:text-xl font-bold">(515) 585-9542</span></span>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 bg-orange-500/5 blur-3xl rounded-lg opacity-50 pointer-events-none" />
            
            <Card className="relative bg-white border-t-4 border-t-slate-900 shadow-xl shadow-slate-200/50 overflow-hidden rounded-sm">
              <CardHeader className="pb-4 border-b border-slate-100 bg-slate-50/30">
                <CardTitle className="text-2xl font-bold text-slate-900 tracking-tight">Request Your Free Inspection</CardTitle>
                <CardDescription className="text-slate-500 text-sm">
                  Complete the form below and we'll connect you with a trusted local professional for a scientific crawl space evaluation.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="mb-6">
                  <Link to="#" className="text-sm font-medium text-primary hover:underline flex items-center gap-2">
                    Learn more about crawl space solutions
                    <ArrowRight className="size-3" />
                  </Link>
                </div>
                <FormProvider formKey="free_inspection_form_contact" formId="free_inspection_form_contact" sectionName="claim-your-free-inspection" defaultValues={{
                full_name: "",
                phone: "",
                zip_code: "",
                preferred_date: "",
                additional_details: ""
              }} validationRules={{
                full_name: {
                  required: true
                },
                phone: {
                  required: true,
                  pattern: "phone"
                },
                zip_code: {
                  required: true,
                  pattern: "^\\d{5}$"
                }
              }} submitText="Request Inspection Referral" successMessage="Thank you! Your request has been received. We'll connect you with a trusted local professional shortly.">
                  <InspectionForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}